/* ============================================================
   BLUE SALON — storefront form intake

   Posts the contact, fitting-room and brand-enquiry forms to the
   Firebase submitForm endpoint instead of Shopify's /contact.

   Progressive enhancement, deliberately: every form keeps its native
   Shopify action and its contact[...] field names, so if this file is
   blocked, fails to load, or the endpoint setting is cleared in the
   theme editor, the browser submits to Shopify exactly as it did
   before and no enquiry is lost. This file only takes over when there
   is an endpoint to take over for.

   Two things stay OUT of this file on purpose:
     - Field mapping lives in the markup (data-bs-key), so adding a
       field to a section never means editing this file.
     - Every user-facing string comes from a data- attribute filled by
       Liquid. None are written here, or they would render in English
       on the Arabic storefront.
   ============================================================ */
(function () {
  "use strict";

  var BS = (window.BS = window.BS || {});

  var TIMEOUT_MS = 15000;
  var RECAPTCHA_TIMEOUT_MS = 4000;

  /* ── ids ────────────────────────────────────────────────────
     One id per form instance, reused across retries: the endpoint
     keys the Firestore document on it, so a failed-then-retried
     send updates one document instead of double-booking a fitting
     room. Only a success mints a new one. */
  function uuid() {
    if (window.crypto && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    if (window.crypto && typeof crypto.getRandomValues === "function") {
      var b = new Uint8Array(16);
      crypto.getRandomValues(b);
      b[6] = (b[6] & 0x0f) | 0x40; /* version 4 */
      b[8] = (b[8] & 0x3f) | 0x80; /* variant   */
      var h = [];
      for (var i = 0; i < 16; i++) h.push((b[i] + 0x100).toString(16).slice(1));
      return (
        h.slice(0, 4).join("") + "-" + h.slice(4, 6).join("") + "-" +
        h.slice(6, 8).join("") + "-" + h.slice(8, 10).join("") + "-" +
        h.slice(10, 16).join("")
      );
    }
    /* Last resort: not cryptographically strong, but this is a dedupe
       key, not a secret. */
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  /* ── payload ────────────────────────────────────────────────
     Only [data-bs-key] inputs travel to Firebase. The hidden
     form_type / utf8 / contact[tags] inputs that Shopify's own form
     needs carry no key, so they stay behind — the endpoint rejects
     unknown fields. Empty optional values are omitted rather than
     sent as ""; required ones are caught by the browser first and by
     the endpoint second. */
  function fieldsFrom(form) {
    var nodes = form.querySelectorAll("[data-bs-key]");
    var out = {};
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var value = el.value == null ? "" : String(el.value).trim();
      if (value !== "") out[el.getAttribute("data-bs-key")] = value;
    }
    return out;
  }

  function honeypotValue(form) {
    var hp = form.querySelector("[data-bs-honeypot]");
    return hp && hp.value ? String(hp.value) : "";
  }

  /* ── notes and field errors ─────────────────────────────────
     Every element involved is already in the DOM, rendered by Liquid
     and hidden; this only flips visibility and fills text the server
     wrote. */
  function scopeOf(form) {
    return form.closest("[data-bs-form-scope]") || form.parentElement || document;
  }

  function showNote(el, text) {
    if (!el) return;
    if (text) el.textContent = text;
    el.hidden = false;
  }

  function clearErrors(form) {
    var note = form.querySelector("[data-bs-error]");
    if (note) note.hidden = true;

    var slots = form.querySelectorAll("[data-bs-field-error]");
    for (var i = 0; i < slots.length; i++) {
      slots[i].hidden = true;
      slots[i].textContent = "";
    }
    var keyed = form.querySelectorAll("[data-bs-key]");
    for (var j = 0; j < keyed.length; j++) {
      keyed[j].removeAttribute("aria-invalid");
      keyed[j].removeAttribute("aria-describedby");
    }
  }

  function showFieldErrors(form, fields) {
    var first = null;
    for (var key in fields) {
      if (!Object.prototype.hasOwnProperty.call(fields, key)) continue;
      var input = form.querySelector('[data-bs-key="' + key + '"]');
      if (!input) continue;
      var slot = form.querySelector('[data-bs-field-error="' + key + '"]');
      if (slot) {
        slot.textContent = fields[key];
        slot.hidden = false;
        if (slot.id) input.setAttribute("aria-describedby", slot.id);
      }
      input.setAttribute("aria-invalid", "true");
      if (!first) first = input;
    }
    if (first && typeof first.focus === "function") first.focus();
    return first;
  }

  /* ── terminal states ────────────────────────────────────────── */
  function succeed(form) {
    var note = form.querySelector("[data-bs-error]");
    if (note) note.hidden = true;
    clearErrors(form);

    var success = scopeOf(form).querySelector("[data-bs-success]");
    if (success) {
      success.hidden = false;
      /* role="status" announces it; focus moves the caret there too, so
         keyboard users are not left at a button that vanished. */
      if (success.hasAttribute("tabindex") && typeof success.focus === "function") {
        success.focus();
      }
      var reduced =
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      try {
        success.scrollIntoView({
          block: "nearest",
          behavior: reduced ? "auto" : "smooth"
        });
      } catch (e) {
        success.scrollIntoView();
      }
    }

    if (form.hasAttribute("data-bs-hide-form")) {
      form.hidden = true;
    } else {
      form.reset();
    }
  }

  function fail(form, kind) {
    var note = form.querySelector("[data-bs-error]");
    var text =
      form.getAttribute("data-bs-error-" + kind) ||
      form.getAttribute("data-bs-error-network");
    showNote(note, text);
    if (note && typeof note.scrollIntoView === "function") {
      note.scrollIntoView({ block: "nearest" });
    }
  }

  /* ── reCAPTCHA v3 ───────────────────────────────────────────
     One token per submission, with an action naming the form it came
     from — the endpoint checks that action, so a token minted on the
     contact page cannot be replayed against the fitting-room form.

     Fails open, at every step: no site key, no Google script, a
     rejected execute(), or a challenge slower than the timeout all
     resolve to an empty token and the submission goes ahead without
     one. The endpoint decides what that is worth — it records the gap
     while in monitor mode, and refuses it once enforcement is on.
     A form must never be held hostage by a third-party script. */
  function recaptchaToken(form) {
    var key = (form.getAttribute("data-bs-recaptcha-key") || "").trim();
    var ready = window.grecaptcha && typeof window.grecaptcha.execute === "function";
    if (!key || !ready) return Promise.resolve("");

    var action = "submit_" + String(form.getAttribute("data-bs-form") || "").replace(/[^A-Za-z0-9_]/g, "");

    return new Promise(function (resolve) {
      var settled = false;
      function done(token) {
        if (settled) return;
        settled = true;
        resolve(typeof token === "string" ? token : "");
      }
      window.setTimeout(done, RECAPTCHA_TIMEOUT_MS);
      try {
        window.grecaptcha.ready(function () {
          try {
            window.grecaptcha.execute(key, { action: action }).then(done, done);
          } catch (e) {
            done();
          }
        });
      } catch (e) {
        done();
      }
    });
  }

  /* ── submit ─────────────────────────────────────────────────── */
  function busy(form, isBusy) {
    var button = form.querySelector('button[type="submit"], input[type="submit"]');
    if (!button) return;
    if (isBusy) {
      var sending = form.getAttribute("data-bs-sending");
      if (sending && !button.hasAttribute("data-bs-label")) {
        button.setAttribute("data-bs-label", button.textContent);
        button.textContent = sending;
      }
      button.disabled = true;
    } else {
      var original = button.getAttribute("data-bs-label");
      if (original !== null) {
        button.textContent = original;
        button.removeAttribute("data-bs-label");
      }
      button.disabled = false;
    }
  }

  function send(form, endpoint) {
    if (form.dataset.bsBusy === "1") return;
    form.dataset.bsBusy = "1";
    busy(form, true);
    clearErrors(form);

    if (!form.dataset.bsId) form.dataset.bsId = uuid();

    /* The button is already in its sending state, so the score is
       collected inside the same wait the visitor is already seeing. */
    recaptchaToken(form).then(
      function (token) {
        post(form, endpoint, token);
      },
      function () {
        /* The helper resolves rather than rejects, but a stuck "Sending…"
           button would be the worst possible failure here. */
        post(form, endpoint, "");
      }
    );
  }

  function post(form, endpoint, captchaToken) {
    var controller = window.AbortController ? new AbortController() : null;
    var timer = window.setTimeout(function () {
      if (controller) controller.abort();
    }, TIMEOUT_MS);

    var payload = {
      form_type: form.getAttribute("data-bs-form"),
      submission_id: form.dataset.bsId,
      fields: fieldsFrom(form),
      meta: {
        page_url: window.location.href,
        referrer: document.referrer || "",
        locale: document.documentElement.getAttribute("lang") || "en"
      },
      fax_number: honeypotValue(form),
      recaptcha_token: captchaToken || ""
    };

    var options = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    };
    if (controller) options.signal = controller.signal;

    var status = 0;
    window
      .fetch(endpoint, options)
      .then(function (response) {
        status = response.status;
        /* A 500 can carry an HTML error page rather than JSON, so a parse
           failure must not be read as a network failure. */
        return response.json().catch(function () {
          return {};
        });
      })
      .then(function (body) {
        if (status === 200 && body && body.ok) {
          /* Mint a fresh id: this form instance is done, and anything
             submitted after a reset is a new enquiry. */
          form.dataset.bsId = "";
          succeed(form);
          return;
        }
        if (status === 400 && body && body.fields) {
          showFieldErrors(form, body.fields);
          fail(form, "validation");
          return;
        }
        fail(form, status === 429 ? "rate" : "network");
      })
      .catch(function () {
        /* Offline, DNS, CORS, or the abort above. */
        fail(form, "network");
      })
      .then(function () {
        window.clearTimeout(timer);
        form.dataset.bsBusy = "0";
        busy(form, false);
      });
  }

  /* ── wiring ─────────────────────────────────────────────────── */
  function initForms() {
    var forms = document.querySelectorAll("[data-bs-form]");
    for (var i = 0; i < forms.length; i++) {
      (function (form) {
        if (form.dataset.bsReady === "1") return;

        var endpoint = (form.getAttribute("data-bs-endpoint") || "").trim();
        if (!endpoint) return; /* no endpoint configured: leave the native Shopify submit alone */

        form.dataset.bsReady = "1";

        /* No novalidate here on purpose: the browser's own constraint
           validation blocks an invalid submit before this handler runs, which
           is how these forms already behaved. The endpoint's 400 response is
           the backstop for what the browser cannot check, such as an option
           that is not in the allowed list. */
        form.addEventListener("submit", function (event) {
          event.preventDefault();
          send(form, endpoint);
        });
      })(forms[i]);
    }
  }

  /* Shopify redirects back with ?contact_posted=true after a native
     submit — which is what happens if this script was absent when the
     visitor pressed send. Show the same success state it would have. */
  function initNativeReturn() {
    if (window.location.search.indexOf("contact_posted=true") === -1) return;
    var forms = document.querySelectorAll("[data-bs-form]");
    for (var i = 0; i < forms.length; i++) succeed(forms[i]);
  }

  function init() {
    initForms();
    initNativeReturn();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  /* Re-wire after a theme-editor section re-render. */
  document.addEventListener("shopify:section:load", initForms);

  BS.forms = { init: initForms };
})();
